import { Container, Text, TextStyle, Graphics } from 'pixi.js';
import gsap from 'gsap';
import { COLORS, FONTS, SPACING } from '../constants/theme';
import { UIInput } from './UIInput';
import { WarriorCardSprite } from './WarriorCardSprite';
import type { WarriorDefinition, WarriorInstance } from '@/types';

const MAX_SELECTIONS = 3;
const WARRIOR_CARD_ASPECT_RATIO = 0.7; // width / height - matches game board cards

export interface WarriorSelectionPanelOptions {
  /** Available warriors to choose from */
  warriors: readonly WarriorDefinition[];
  /** Player color for styling (COLORS.PLAYER1 or COLORS.PLAYER2) */
  playerColor: number;
  /** Initial player name */
  playerName: string;
  /** Initial player rating */
  playerRating: number;
  /** Callback when selection changes */
  onSelectionChange?: (selections: string[]) => void;
  /** Callback when name changes */
  onNameChange?: (name: string) => void;
}

/**
 * Reusable warrior selection panel with player info and warrior gallery
 */
export class WarriorSelectionPanel extends Container {
  private options: WarriorSelectionPanelOptions;

  // Player info
  private playerInfoContainer: Container | null = null;
  private ratingText: Text | null = null;

  // Gallery
  private galleryContainer: Container | null = null;
  private warriorCards: Map<string, Container> = new Map();

  // Selection state
  private selections: string[] = [];
  private playerName: string;
  private playerRating: number;

  // Name input (using @pixi/ui Input)
  private nameInput: UIInput | null = null;

  // Dimensions
  private containerWidth = 500;
  private playerInfoHeight = 60;
  private cardWidth = 80;
  private cardHeight = 100;

  constructor(options: WarriorSelectionPanelOptions) {
    super();
    this.options = options;
    this.playerName = options.playerName;
    this.playerRating = options.playerRating;
  }

  /**
   * Build the panel with given dimensions
   */
  build(containerWidth: number, _playerInfoHeight: number, _galleryHeight: number): void {
    this.containerWidth = containerWidth;
    // Fixed height for simplified player info (rating + name input)
    this.playerInfoHeight = 60;

    this.removeChildren();
    this.warriorCards.clear();

    // Player info container (rating and name input only)
    this.playerInfoContainer = new Container();
    this.addChild(this.playerInfoContainer);
    this.buildPlayerInfo();

    // Gallery container - below player info
    this.galleryContainer = new Container();
    this.galleryContainer.y = this.playerInfoHeight + SPACING.SM;
    this.addChild(this.galleryContainer);
    this.buildGallery();
  }

  private buildPlayerInfo(): void {
    if (!this.playerInfoContainer) return;

    const { playerColor } = this.options;

    // Rating text - positioned at top left
    const ratingStyle = new TextStyle({
      fontFamily: FONTS.PRIMARY,
      fontSize: FONTS.SIZES.SM,
      fill: playerColor,
    });
    this.ratingText = new Text({ text: `Rating: ${this.playerRating}`, style: ratingStyle });
    this.ratingText.x = 0;
    this.ratingText.y = 0;
    this.playerInfoContainer.addChild(this.ratingText);

    // Name input will be created after layout settles (in createNameInput)
  }

  private buildGallery(): void {
    if (!this.galleryContainer) return;

    const { warriors } = this.options;
    const containerWidth = this.containerWidth;

    // Calculate card size - 5 cards per row
    const cardsPerRow = 5;
    const cardGap = SPACING.SM;
    const totalGaps = (cardsPerRow - 1) * cardGap;
    this.cardWidth = Math.floor((containerWidth - totalGaps) / cardsPerRow);
    this.cardHeight = this.cardWidth / WARRIOR_CARD_ASPECT_RATIO;

    warriors.forEach((warrior, index) => {
      const row = Math.floor(index / cardsPerRow);
      const col = index % cardsPerRow;

      const isSelected = this.selections.includes(warrior.id);
      const mockInstance = this.createMockWarriorInstance(warrior);

      const card = new WarriorCardSprite({
        warrior: mockInstance,
        width: this.cardWidth,
        height: this.cardHeight,
        isSelected,
        isCurrentTurn: true, // Always show full opacity in selection
        onSelect: () => this.toggleWarriorSelection(warrior.id),
      });

      // WarriorCardSprite is positioned from top-left, not centered
      card.x = col * (this.cardWidth + cardGap);
      card.y = row * (this.cardHeight + cardGap);

      // Add selection badge overlay
      const badgeSize = Math.max(12, Math.min(16, this.cardWidth * 0.15));
      const selectionBadge = new Container();
      selectionBadge.x = this.cardWidth - badgeSize - 4;
      selectionBadge.y = badgeSize + 4;
      selectionBadge.visible = isSelected;

      const badgeCircle = new Graphics();
      badgeCircle.circle(0, 0, badgeSize);
      badgeCircle.fill({ color: COLORS.SUCCESS });
      selectionBadge.addChild(badgeCircle);

      const badgeNumber = new Text({
        text: isSelected ? String(this.selections.indexOf(warrior.id) + 1) : '1',
        style: { fontFamily: FONTS.PRIMARY, fontSize: badgeSize * 1.2, fontWeight: 'bold', fill: 0xffffff },
      });
      badgeNumber.anchor.set(0.5);
      selectionBadge.addChild(badgeNumber);
      card.addChild(selectionBadge);

      // Store references for updating
      (card as any).selectionBadge = selectionBadge;
      (card as any).badgeNumber = badgeNumber;
      (card as any).warriorId = warrior.id;
      (card as any).mockInstance = mockInstance;

      // Add hover effect
      card.on('pointerover', () => gsap.to(card.scale, { x: 1.05, y: 1.05, duration: 0.1 }));
      card.on('pointerout', () => gsap.to(card.scale, { x: 1, y: 1, duration: 0.1 }));

      this.galleryContainer!.addChild(card);
      this.warriorCards.set(warrior.id, card);
    });
  }

  /**
   * Create a mock WarriorInstance from a WarriorDefinition for display purposes
   */
  private createMockWarriorInstance(warrior: WarriorDefinition): WarriorInstance {
    return {
      instanceId: `selection-${warrior.id}`,
      definitionId: warrior.id,
      ownerId: 'player1',
      currentHealth: warrior.stats.health,
      position: null,
      attackCooldown: 0, // Show cooldown needed (0/frequency)
      healCooldown: 0, // Show cooldown needed (0/frequency)
      dashCooldown: 0, // Show cooldown needed (0/frequency)
      definition: warrior,
    };
  }

  private toggleWarriorSelection(warriorId: string): void {
    const index = this.selections.indexOf(warriorId);
    if (index >= 0) {
      this.selections.splice(index, 1);
    } else if (this.selections.length < MAX_SELECTIONS) {
      this.selections.push(warriorId);
    }

    this.updateWarriorCards();
    this.options.onSelectionChange?.(this.selections);
  }

  private updateWarriorCards(): void {
    for (const [id, card] of this.warriorCards) {
      const selectionIndex = this.selections.indexOf(id);
      const isSelected = selectionIndex >= 0;
      const selectionBadge = (card as any).selectionBadge as Container;
      const badgeNumber = (card as any).badgeNumber as Text;
      const mockInstance = (card as any).mockInstance as WarriorInstance;

      // Update selection badge
      selectionBadge.visible = isSelected;
      if (isSelected) {
        badgeNumber.text = String(selectionIndex + 1);
      }

      // Update the WarriorCardSprite
      if (card instanceof WarriorCardSprite) {
        card.update(mockInstance, isSelected, true);
      }
    }
  }

  /**
   * Create name input - call after layout settles
   */
  createNameInput(): void {
    if (!this.playerInfoContainer) return;

    // Remove existing input if any
    this.removeNameInput();

    const { playerColor } = this.options;
    const inputWidth = Math.min(200, this.containerWidth);

    this.nameInput = new UIInput({
      width: inputWidth,
      height: 36,
      placeholder: 'Enter your name',
      value: this.playerName,
      maxLength: 12,
      borderColor: playerColor,
      onChange: (value) => {
        this.playerName = value;
        this.options.onNameChange?.(this.playerName);
      },
    });
    this.nameInput.x = 0;
    this.nameInput.y = 20; // Below rating text
    this.playerInfoContainer.addChild(this.nameInput);
  }

  /**
   * Remove name input
   */
  removeNameInput(): void {
    if (this.nameInput) {
      this.nameInput.destroy();
      this.nameInput = null;
    }
  }

  /**
   * Hide name input (for overlay purposes)
   */
  hideNameInput(): void {
    if (this.nameInput) {
      this.nameInput.visible = false;
    }
  }

  /**
   * Show name input
   */
  showNameInput(): void {
    if (this.nameInput) {
      this.nameInput.visible = true;
    }
  }

  // Public getters/setters

  getSelections(): string[] {
    return [...this.selections];
  }

  setSelections(selections: string[]): void {
    this.selections = selections.slice(0, MAX_SELECTIONS);
    this.updateWarriorCards();
  }

  getPlayerName(): string {
    return this.nameInput?.getValue().trim() || this.playerName;
  }

  setPlayerName(name: string): void {
    this.playerName = name;
    if (this.nameInput) {
      this.nameInput.setValue(name);
    }
  }

  getPlayerRating(): number {
    return this.playerRating;
  }

  setPlayerRating(rating: number): void {
    this.playerRating = rating;
    if (this.ratingText) {
      this.ratingText.text = `Rating: ${rating}`;
    }
  }

  isReady(): boolean {
    return this.selections.length === MAX_SELECTIONS;
  }

  getPlayerInfoContainer(): Container | null {
    return this.playerInfoContainer;
  }

  dispose(): void {
    this.removeNameInput();
    this.removeChildren();
    this.warriorCards.clear();
  }
}
